import React from 'react';
import {
  X,
  Heart,
  ShieldCheck,
  Lock,
  Star,
  CreditCard,
  MessageCircle,
  CalendarCheck,
  LifeBuoy,
  Sparkles,
  CheckCircle2,
  Compass,
  ArrowRight
} from 'lucide-react';

interface AboutModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenSafety?: () => void;
  onExploreCompanions?: () => void;
}

export const AboutModal: React.FC<AboutModalProps> = ({
  isOpen,
  onClose,
  onOpenSafety,
  onExploreCompanions
}) => {
  if (!isOpen) return null;

  const whyItems = [
    { icon: Heart, label: 'Companionship for Every Occasion', color: 'text-pink-600 bg-pink-50' },
    { icon: ShieldCheck, label: 'Verified Profiles', color: 'text-purple-600 bg-purple-50' },
    { icon: Lock, label: 'Privacy & Safety First', color: 'text-emerald-600 bg-emerald-50' },
    { icon: Star, label: 'Ratings & Reviews', color: 'text-amber-600 bg-amber-50' },
    { icon: CreditCard, label: 'Secure Online Payments', color: 'text-blue-600 bg-blue-50' },
    { icon: MessageCircle, label: 'Safe In-App Communication', color: 'text-rose-600 bg-rose-50' },
    { icon: CalendarCheck, label: 'Easy Booking & Availability', color: 'text-violet-600 bg-violet-50' },
    { icon: LifeBuoy, label: 'Dedicated Customer Support', color: 'text-indigo-600 bg-indigo-50' }
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-950/70 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
      <div className="relative w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-pink-100 overflow-hidden my-6">
        
        {/* Modal Header */}
        <div className="relative bg-gradient-to-r from-slate-900 via-purple-950 to-slate-900 p-6 sm:p-8 text-white">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors cursor-pointer"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>

          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-pink-500 to-rose-600 flex items-center justify-center text-white shadow-md">
              <Heart className="w-5 h-5 fill-white text-white" />
            </div>
            <div className="flex items-center tracking-tight">
              <span className="font-extrabold text-2xl text-white">PairUp</span>
              <span className="font-extrabold text-2xl text-pink-500">Today</span>
              <span className="ml-1 text-xl text-rose-400">❤️</span>
            </div>
          </div>

          <h2 className="text-xl sm:text-2xl font-extrabold text-white">
            About PairUpToday
          </h2>
          <p className="text-xs sm:text-sm text-pink-200/90 mt-1 max-w-xl">
            A modern companionship platform designed to help people find the right companion for meaningful moments and memorable occasions.
          </p>
        </div>

        {/* Modal Scrollable Body */}
        <div className="p-6 sm:p-8 space-y-6 max-h-[75vh] overflow-y-auto">
          
          {/* Paragraph 1 & 2 */}
          <div className="space-y-3 text-sm sm:text-base text-slate-700 leading-relaxed">
            <p>
              PairUpToday is a modern companionship platform designed to help people find the right companion for meaningful moments and memorable occasions.
            </p>
            <p>
              Whether you are looking for someone to accompany you to a date, social event, party, travel experience, photoshoot, wedding, or simply want good company, PairUpToday makes discovering and connecting with companions simple, convenient, and secure.
            </p>
            <p>
              Our platform is built around choice, privacy, safety, and trust. We aim to provide a comfortable environment where users can explore verified profiles, view ratings and reviews, check availability, communicate through the platform, and make bookings with confidence.
            </p>
          </div>

          {/* Mission Box */}
          <div className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-5 sm:p-6 border border-pink-100/90 space-y-2.5">
            <div className="flex items-center gap-2 text-pink-700 font-extrabold text-xs uppercase tracking-wider">
              <Sparkles className="w-4 h-4 text-pink-600" />
              <span>Our Mission</span>
            </div>
            <p className="text-xs sm:text-sm text-slate-600 font-medium">
              Our mission is simple:
            </p>
            <blockquote className="text-base sm:text-lg font-bold text-slate-900 border-l-4 border-pink-500 pl-3 italic">
              “To make finding the right companion easier, safer, and more comfortable for everyone.”
            </blockquote>
            <p className="text-xs sm:text-sm text-slate-600 leading-relaxed pt-1">
              We believe that nobody should have to experience important moments alone. PairUpToday brings people together so they can meet, connect, share experiences, and create memorable moments.
            </p>
          </div>

          {/* Why PairUpToday */}
          <div className="space-y-3">
            <h3 className="font-extrabold text-base sm:text-lg text-slate-900 flex items-center gap-2">
              <span>Why PairUpToday?</span>
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {whyItems.map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div
                    key={idx}
                    className="flex items-center gap-3 p-3 rounded-xl bg-slate-50 border border-slate-100 hover:border-pink-200 transition-colors"
                  >
                    <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center shrink-0`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <span className="text-xs sm:text-sm font-bold text-slate-800">
                      {item.label}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Our Promise */}
          <div className="bg-slate-900 text-white rounded-2xl p-5 sm:p-6 space-y-3">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md bg-pink-500/20 text-pink-300 text-xs font-bold">
              <CheckCircle2 className="w-3.5 h-3.5 text-pink-400" />
              <span>Our Promise</span>
            </div>
            <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
              At PairUpToday, we are committed to maintaining a respectful, safe, transparent, and inclusive platform. We follow strict community guidelines and do not allow harassment, exploitation, fraudulent profiles, or services that violate our platform rules.
            </p>
            <div className="pt-2 text-pink-400 font-extrabold text-sm flex items-center gap-1.5">
              <span>PairUpToday — Find Your Perfect Companion.</span>
              <Heart className="w-4 h-4 fill-pink-500 text-pink-500 inline" />
            </div>
          </div>

        </div>

        {/* Modal Footer Actions */}
        <div className="p-4 sm:p-6 bg-slate-50 border-t border-slate-100 flex flex-col sm:flex-row items-center justify-between gap-3">
          {onOpenSafety && (
            <button
              type="button"
              onClick={() => {
                onClose();
                onOpenSafety();
              }}
              className="text-xs font-bold text-slate-600 hover:text-pink-600 flex items-center gap-1.5 transition-colors cursor-pointer"
            >
              <ShieldCheck className="w-4 h-4 text-emerald-600" />
              <span>Read Safety & Verification Rules</span>
            </button>
          )}

          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2.5 rounded-xl border border-slate-200 text-slate-700 hover:bg-slate-100 text-xs sm:text-sm font-semibold transition-colors w-full sm:w-auto cursor-pointer"
            >
              Close
            </button>
            <button
              type="button"
              onClick={() => {
                onClose();
                if (onExploreCompanions) onExploreCompanions();
              }}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-pink-600 to-rose-600 hover:from-pink-700 hover:to-rose-700 text-white text-xs sm:text-sm font-bold shadow-md shadow-pink-500/20 transition-all flex items-center justify-center gap-2 w-full sm:w-auto cursor-pointer"
            >
              <span>Explore Companions</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
